import type { CampaignPaymentListStatus } from '@/types/campaign-report'

export const ALL_PAYMENT_LIST_STATUSES: CampaignPaymentListStatus[] = [
  'pending',
  'paid',
  'expired',
  'canceled',
  'failed',
]

export const DEFAULT_PEDIDOS_STATUSES: CampaignPaymentListStatus[] = ['paid']

export const PAYMENT_STATUS_LABELS: Record<CampaignPaymentListStatus, string> = {
  pending: 'Pendente',
  paid: 'Pago',
  expired: 'Expirado',
  canceled: 'Cancelado',
  failed: 'Falhou',
}

/** Valor único do select de status na lista de pedidos. */
export type PedidosStatusFilterValue =
  | CampaignPaymentListStatus
  | 'all'

export const PEDIDOS_STATUS_FILTER_OPTIONS: ReadonlyArray<{
  value: PedidosStatusFilterValue
  label: string
}> = [
  { value: 'paid', label: 'Pago' },
  { value: 'pending', label: 'Pendente' },
  { value: 'expired', label: 'Expirado' },
  { value: 'canceled', label: 'Cancelado' },
  { value: 'failed', label: 'Falhou' },
  { value: 'all', label: 'Todos os status' },
]

export function isSameStatusSelection(
  a: CampaignPaymentListStatus[],
  b: CampaignPaymentListStatus[]
): boolean {
  if (a.length !== b.length) return false
  const setB = new Set(b)
  return a.every((status) => setB.has(status))
}

export function isAllStatusesSelected(statuses: CampaignPaymentListStatus[]): boolean {
  return isSameStatusSelection(statuses, ALL_PAYMENT_LIST_STATUSES)
}

export function statusesFromFilterValue(
  value: PedidosStatusFilterValue
): CampaignPaymentListStatus[] {
  if (value === 'all') {
    return [...ALL_PAYMENT_LIST_STATUSES]
  }
  return [value]
}

export function filterValueFromStatuses(
  statuses: CampaignPaymentListStatus[]
): PedidosStatusFilterValue {
  if (isAllStatusesSelected(statuses)) {
    return 'all'
  }
  if (statuses.length === 1) {
    return statuses[0]
  }
  return 'all'
}

export function describeActiveStatusFilter(statuses: CampaignPaymentListStatus[]): string {
  const value = filterValueFromStatuses(statuses)
  if (value === 'all') {
    return 'Todos os status'
  }
  return PAYMENT_STATUS_LABELS[value]
}

export interface PedidosListFiltersState {
  statuses: CampaignPaymentListStatus[]
  dateFrom: string
  dateTo: string
  dateBasis: 'paid' | 'created'
}

export function getDefaultPedidosFilters(): PedidosListFiltersState {
  return {
    statuses: [...DEFAULT_PEDIDOS_STATUSES],
    dateFrom: '',
    dateTo: '',
    dateBasis: 'paid',
  }
}

export function parsePedidosFiltersFromSearchParams(
  searchParams: URLSearchParams
): PedidosListFiltersState {
  const base = getDefaultPedidosFilters()
  const statusStr = searchParams.get('status')
  if (statusStr) {
    const parsed = statusStr
      .split(',')
      .map((s) => s.trim())
      .filter((s): s is CampaignPaymentListStatus =>
        ALL_PAYMENT_LIST_STATUSES.includes(s as CampaignPaymentListStatus)
      )
    if (parsed.length > 0) {
      base.statuses = statusesFromFilterValue(filterValueFromStatuses(parsed))
    }
  }
  const df = searchParams.get('date_from')
  const dt = searchParams.get('date_to')
  if (df) base.dateFrom = df
  if (dt) base.dateTo = dt
  const db = searchParams.get('date_basis')
  if (db === 'paid' || db === 'created') {
    base.dateBasis = db
  }
  return base
}

export function parseSearchQueryFromSearchParams(
  searchParams: URLSearchParams
): string {
  return searchParams.get('q') ?? ''
}

export function writePedidosFiltersToSearchParams(
  filters: PedidosListFiltersState,
  page: number,
  searchQuery: string
): URLSearchParams {
  const next = new URLSearchParams()
  const defaultJoin = DEFAULT_PEDIDOS_STATUSES.slice().sort().join(',')
  const currentJoin = [...filters.statuses].sort().join(',')
  if (currentJoin !== defaultJoin) {
    next.set('status', filters.statuses.join(','))
  }
  if (filters.dateFrom !== '' && filters.dateTo !== '') {
    next.set('date_from', filters.dateFrom)
    next.set('date_to', filters.dateTo)
  }
  if (filters.dateBasis !== 'paid') {
    next.set('date_basis', filters.dateBasis)
  }
  if (searchQuery.trim() !== '') {
    next.set('q', searchQuery.trim())
  }
  if (page > 1) {
    next.set('page', String(page))
  }
  return next
}
